/**
 * DomainDetailPage — 域详情
 * 展示域完整信息；编辑 / 删除操作放在此处（列表行不再直接提供）。
 */
import { useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Spinner,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Field,
} from '@fluentui/react-components'
import { EditRegular, DeleteRegular, GlobeRegular } from '@fluentui/react-icons'
import { domainsApi } from '@/api/domains'
import type { Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'
import { formatDate, formatQuota, formatUsers } from '@/lib/format'

interface DomainFormState {
  name: string
  description: string
  max_quota_bytes: string
  max_users: string
}

const EMPTY_FORM: DomainFormState = {
  name: '',
  description: '',
  max_quota_bytes: '',
  max_users: '',
}

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '360px',
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px',
  },
  deleteName: {
    fontWeight: 600,
    color: tokens.colorStatusDangerForeground1,
  },
})

export default function DomainDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { item, isLoading } = useDetailItem<Domain>(
    ['domains'],
    () => domainsApi.list(),
    id as string,
    'id',
  )

  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState<DomainFormState>(EMPTY_FORM)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const openEdit = (domain: Domain) => {
    setForm({
      name: domain.name,
      description: domain.description ?? '',
      max_quota_bytes:
        domain.max_quota_bytes != null ? String(domain.max_quota_bytes) : '',
      max_users: domain.max_users != null ? String(domain.max_users) : '',
    })
    setEditOpen(true)
  }

  const updateMutation = useMutation({
    mutationFn: (data: Omit<DomainFormState, 'description'> & { description?: string }) =>
      domainsApi.update(Number(id), {
        name: data.name,
        description: data.description || undefined,
        max_quota_bytes: data.max_quota_bytes.trim()
          ? Number(data.max_quota_bytes)
          : undefined,
        max_users: data.max_users.trim() ? Number(data.max_users) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      toast.success('更新成功', '域信息已更新')
      setEditOpen(false)
    },
    onError: (err) =>
      toast.error('更新失败', err instanceof ApiError ? err.message : '更新失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => domainsApi.delete(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      toast.success('删除成功', '域已删除')
      navigate(ROUTES.DOMAINS)
    },
    onError: (err) =>
      toast.error('删除失败', err instanceof ApiError ? err.message : '删除失败'),
  })

  const stateItem = (location.state as { item?: Domain } | null)?.item

  return (
    <>
      <DetailLayout
        loading={isLoading && !stateItem}
        notFound={!isLoading && !item && !stateItem}
        notFoundText="未找到该域"
        title={item?.name ?? stateItem?.name ?? '域详情'}
        subtitle={item?.description ?? stateItem?.description ?? undefined}
        media={<GlobeRegular />}
        badge={
          <Badge appearance="filled" color={(item ?? stateItem)?.active ? 'success' : 'danger'}>
            {(item ?? stateItem)?.active ? '活跃' : '停用'}
          </Badge>
        }
        actions={
          <>
            <Button
              appearance="subtle"
              icon={<EditRegular />}
              onClick={() => openEdit(item ?? (stateItem as Domain))}
            >
              编辑
            </Button>
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              onClick={() => setDeleteOpen(true)}
            >
              删除
            </Button>
          </>
        }
      >
        <DetailSection title="基本信息">
          <DetailRow label="ID" value={item?.id ?? stateItem?.id} />
          <DetailRow label="域名" value={item?.name ?? stateItem?.name} />
          <DetailRow label="描述" value={item?.description ?? stateItem?.description} />
          <DetailRow
            label="状态"
            value={(item ?? stateItem)?.active ? '活跃' : '停用'}
          />
          <DetailRow
            label="最大配额"
            value={formatQuota((item ?? stateItem)?.max_quota_bytes)}
          />
          <DetailRow
            label="最大用户数"
            value={formatUsers((item ?? stateItem)?.max_users)}
          />
          <DetailRow
            label="创建时间"
            value={formatDate((item ?? stateItem)?.created_at)}
          />
        </DetailSection>
      </DetailLayout>

      {/* 编辑对话框 */}
      <Dialog
        open={editOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setEditOpen(false)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>编辑域</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <Field label="域名" required>
                  <Input
                    value={form.name}
                    onChange={(_, d) => setForm((f) => ({ ...f, name: d.value }))}
                  />
                </Field>
                <Field label="描述">
                  <Input
                    value={form.description}
                    onChange={(_, d) =>
                      setForm((f) => ({ ...f, description: d.value }))
                    }
                  />
                </Field>
                <Field label="最大配额 (字节)" hint="留空表示不限">
                  <Input
                    type="number"
                    value={form.max_quota_bytes}
                    onChange={(_, d) =>
                      setForm((f) => ({ ...f, max_quota_bytes: d.value }))
                    }
                  />
                </Field>
                <Field label="最大用户数" hint="留空表示不限">
                  <Input
                    type="number"
                    value={form.max_users}
                    onChange={(_, d) =>
                      setForm((f) => ({ ...f, max_users: d.value }))
                    }
                  />
                </Field>
              </div>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setEditOpen(false)}>取消</Button>
              <Button
                appearance="primary"
                disabled={updateMutation.isPending}
                icon={updateMutation.isPending ? <Spinner size="tiny" /> : undefined}
                onClick={() => updateMutation.mutate(form)}
              >
                保存
              </Button>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除确认 */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setDeleteOpen(false)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定要删除域{' '}
                <span className={styles.deleteName}>{item?.name}</span>{' '}
                吗？此操作不可撤销。
              </Text>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
